import { createColumnHelper, useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { EntityTable } from '@/components/table/entity-table';
import { InfoCard } from '@/components/info-card';
import { Badge } from '@/components/ui/badge';
import { adjustMinimalClassTimeAcrossTZ, CoachViewableStudentGroupWithScheduleAndLevel } from '@airreading/common/data';
import { formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { formatScheduleBreaksForDisplay } from '@/lib/meeting-block-utils';
import { formatStudentGrades } from '@/lib/student-group-utils';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting';

function coachDisplayName(name: string | null | undefined): string {
  return name?.trim() || '-';
}

export function ClassesCard({ classes }: { classes: CoachViewableStudentGroupWithScheduleAndLevel[] }) {
  const columnHelper = createColumnHelper<CoachViewableStudentGroupWithScheduleAndLevel>();

  const columns = [
    columnHelper.accessor((row) => row.classTime, {
      id: 'classTimes',
      header: 'Class times (CT)',
      enableSorting: false,
      cell: ({ getValue }) => {
        const classTime = getValue();
        return (
          <Badge variant="outline" className="bg-[#F1F1F1] font-normal">
            {formatStudentClassTimeAbbr(
              adjustMinimalClassTimeAcrossTZ(
                classTime,
                classTime.schedule.school.org.timeZone,
                FrequentTimeZones.UsCentral,
              ),
            )}
          </Badge>
        );
      },
    }),
    columnHelper.accessor(
      (row) => {
        return row.overrideStartPlainDate || row.classTime?.schedule?.startPlainDate;
      },
      {
        id: 'startDate',
        header: 'Start date',
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? formatPlainDate(value) : '';
        },
      },
    ),
    columnHelper.accessor(
      (row) => {
        return row.overrideEndPlainDate || row.classTime?.schedule?.endPlainDate;
      },
      {
        id: 'endDate',
        header: 'End date',
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? formatPlainDate(value) : '';
        },
      },
    ),
    columnHelper.accessor((row) => row.futureSessions ?? 0, {
      id: 'sessionsLeft',
      header: 'Sessions left',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor((row) => row.students ?? [], {
      id: 'students',
      header: 'Students',
      enableSorting: false,
      cell: ({ getValue }) => {
        return (
          <div className="flex flex-col gap-1">
            {getValue().map((student) => (
              <Badge key={student.id} variant="outline" className="text-left rounded-sm px-1 w-fit">
                {coachDisplayName(student.coachFacingDisplayName)}
              </Badge>
            ))}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.grades ?? [], {
      id: 'grades',
      header: 'Grades',
      enableSorting: false,
      cell: ({ getValue }) => formatStudentGrades(getValue()),
    }),
    columnHelper.accessor((row) => row.classTime?.schedule?.school?.name ?? '', {
      id: 'school',
      header: 'School',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor((row) => row.classTime?.schedule?.school?.org?.name ?? '', {
      id: 'district',
      header: 'District',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),

    columnHelper.accessor((row) => formatScheduleBreaksForDisplay(row.classTime.schedule.scheduleBreaks), {
      id: 'breaks',
      header: 'Break(s)',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),
  ];
  const table = useReactTable({
    data: classes,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <InfoCard headerText="Current groups">
      <EntityTable table={table} emptyStateClassName="translate-y-0" />
    </InfoCard>
  );
}
